import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux';
import { useWorkspaceId } from '../../Hooks/useWorkspaceContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { BrandGAds, BrandGAnalytics, BrandMagento, BrandMeta, BrandShopify, CheckIcon, ChevronDownIcon, CloseIcon, CopyIcon, CustomIntegrationLogo, EditChatIcon, ExternalLinkIcon, HelpIcon, InfoSettings, PlusIcon, RightArrowIcon, TrashIcon } from '../../components/Icons/iconImages';
import Cards from '../../components/Cards';
import {
  useAddGaAccountMutation,
  useAddGoogleadsAccountMutation,
  useCheckAccessInUserBigqueryMutation,
  useGetAnalyticStatusQuery,
  useGetBigQueryDatasetDropdownQuery,
  useLazyGetEventDataQuery,
  useLazyGetFirstGoogleAdsDataQuery,
  useLazyGetGoogleAdsAccountQuery,
  useLazyGetMetaAccountsQuery,
  useLazyGoogleAdsQuery,
  useLazyIntegrationGaAccountQuery,
  useLazyIntegrationQuery,
  useLazyListYoutubeQuery,
  useLazyMetaIntegrationQuery,
  useLazyRemoveGAQuery,
  useLazyRemoveGoogleAdsQuery,
  useDeleteGoogleAdsIntegrationMutation,
  useLazyRemoveMetaQuery,
  useDeleteMetaIntegrationMutation,
  useDeleteGAIntegrationMutation,
  useDeleteShopifyIntegrationMutation,
  useDeleteYoutubeIntegrationMutation,
  useLazyRemoveShopifyQuery,
  useLazyRemoveYoutubeQuery,
  useLazyShopifyDirectIntegrationQuery,
  useLazyVerifyGoogleAdsTokenQuery,
  useLazyVerifyGoogleTokenQuery,
  useLazyVerifyShopifyQuery,
  useLazyVerifyYoutubeTokenQuery,
  useLazyYoutubeIntegrationQuery,
  useMetaVerifyMutation,
  useSaveBigQueryProjectIdMutation,
  useSelectDatasetForToolMutation,
  useGetBigQueryProjectIdStatusQuery,
  useSaveTableNameUsingDataSetMutation,
  usePostSchemaDesMutation,
  useDeleteCustomBigQueryIntegrationMutation,
  useListTablesInDatasetQuery,
  useGetCustomNameQuery,
  useEditCustomNameMutation,
  useGetListAnalyticsAccountsQuery,
  useGetAllToolsAccountsForReselectQuery,
} from '../../services/integrationSlice';
import useModal from '../../Hooks/useModal';
import { Checkbox, Select, Modal as AntdModal, Drawer, Steps, Spin, Badge as AntBadge } from 'antd';
import { Button } from '../../components/Buttons';
import { useLocation, useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useGetGeneralSettingsQuery } from '../../services/settingsSlice';
import InputField from '../../components/InputField';
import { Modal, ModalBottomOverlay } from '../../components/Modal';
import Badge, { PillsBadge } from '../../components/Badge';
import IconRings from '../../components/IconRings';
import gsap from 'gsap';
import { useGetCurrentPlanQuery } from '../../services/stripe';
import { maskName } from '../../helper';
import DynamicTable from '../../components/Table/NestedTable';
import { toast } from '../../components/Toast/toast';
import { CustomSegmented } from '../../components/HeaderNav/Tabs';
import useIsInIframe from '../../Hooks/useIsInIframe';

export const IntegrationComponent = ({
  signup = false,
  className = '',
  style = {},
  iconMap,
  control,
  setError,
  reset,
  getValues,
  watch,
}) => {
  const isInIframe = useIsInIframe() ? "Ask AI" : "Oogwai";
  const [loading, setLoading] = useState(false);
  const [oauthProcessed, setOauthProcessed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [addGaAccount] = useAddGaAccountMutation();
  const [accountOptions, setAccountOptions] = useState([]);
  const isGoogleAds = location?.pathname && location?.pathname?.split('/')[2] === 'google-ads';

  // ? ask to select which account to add in the workspace, will run after redirection
  const [getIntegrationAccountNames, { isLoading: getIntegrationAccountNamesLoading }] = useLazyIntegrationGaAccountQuery();
  const [getGoogleAdsAccountNames, { isLoading: getGoogleAdsAccountNamesLoading }] = useLazyGetGoogleAdsAccountQuery();
  const [addGoogleAdsAccount] = useAddGoogleadsAccountMutation();

  const workspace_id = useWorkspaceId() || localStorage.getItem("workspace_id");
  const { getCurrentWorkspaceObjectId } = useWorkspace();

  const token = localStorage.getItem("oogw.ai_user");

  const { integrations } = useSelector((state) => state.integrations);

  // ? MODAL FOR CHECKING SYNC AND DETAILS OF INTEGRATION
  const {
    isOpen: isDetailsOverlayOpen,
    openModal: openDetailsOverlayModal,
    closeModal: closeDetailsOverlayModal,
    modalData: detailsOverlayModalData
  } = useModal();

  // ? MODAL AFTER REDIRECTION
  const {
    isOpen: isOauthOpen,
    openModal: oauthOpenModal,
    closeModal: oauthCloseModal,
    modalData: oauthModalData
  } = useModal();

  // ? MODAL FOR DELETING INTEGRATION
  const {
    isOpen: isDeleteOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
    modalData: deleteModalData
  } = useModal();

  // ? MODAL FOR SHOPIFY INTEGRATION
  const {
    isOpen: isShopifyOpen,
    openModal: openShopifyModal,
    closeModal: closeShopifyModal,
    modalData: shopifyModalData
  } = useModal();

  // ? MODAL AFTER REDIRECTION
  const {
    isOpen: isAddAccountToWorkspaceOpen,
    openModal: openAddAccountToWorkspaceModal,
    closeModal: closeAddAccountToWorkspaceModal,
    modalData: addAccountToWorkspaceModalData
  } = useModal();

  const [shopifyTimer, setShopifyTimer] = useState(5);
  const shopifyTimerRef = useRef(null);

  useEffect(() => {
    if (isShopifyOpen) {
      localStorage.setItem('shopify_internal', 'true');
      setShopifyTimer(5);

      // Clear any existing timer
      if (shopifyTimerRef.current) {
        clearInterval(shopifyTimerRef.current);
      }

      // Start new timer
      shopifyTimerRef.current = setInterval(() => {
        setShopifyTimer((prev) => {
          if (prev <= 1) {
            clearInterval(shopifyTimerRef.current);
            shopifyTimerRef.current = null;
            // Redirect when timer reaches 0
            window.open('https://apps.shopify.com/oogwai-analytics', '_blank');
            closeShopifyModal();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (shopifyTimerRef.current) {
        clearInterval(shopifyTimerRef.current);
        shopifyTimerRef.current = null;
      }
    };
  }, [isShopifyOpen]);

  const [lazyIntegration] = useLazyIntegrationQuery();
  const [gooleVerify] = useLazyVerifyGoogleTokenQuery();
  const [removeGA] = useLazyRemoveGAQuery();
  const [eventData] = useLazyGetEventDataQuery(); // GET GA THE EVENT DATA
  const { data: getStatus, refetch, error, isLoading: isLoadingStatus } = useGetAnalyticStatusQuery(workspace_id, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true
  });
  const { data: listAnalyticsData } = useGetListAnalyticsAccountsQuery({});

  const { data: getUserData } = useGetGeneralSettingsQuery('');
  const userEmail = getUserData?.settings?.email || 'No email id found';

  const getEmail = localStorage.getItem("oogw.ai_user_email") || userEmail;
  const ownUser = getEmail && getEmail === "demo@oogw.ai" ? true : false;


  const defaultValues = {
    accounts: '',
  };

  const {
    handleSubmit,
    control: gaControl,
    formState: { errors },
  } = useForm({
    defaultValues,
  });

  // GA4
  const [deleteGAIntegration] = useDeleteGAIntegrationMutation();

  // shopify 
  const [integrateShopify, { isLoading: isIntegratingShopify }] = useLazyShopifyDirectIntegrationQuery();
  const [shopifyVerify] = useLazyVerifyShopifyQuery();
  const [removeShopify] = useLazyRemoveShopifyQuery();
  const [deleteShopifyIntegration] = useDeleteShopifyIntegrationMutation();

  // meta
  const [metaIntegration] = useLazyMetaIntegrationQuery();
  const [removeMeta] = useLazyRemoveMetaQuery();
  const [deleteMetaIntegration] = useDeleteMetaIntegrationMutation();
  const [metaVerify] = useMetaVerifyMutation();
  const [getMetaAccounts] = useLazyGetMetaAccountsQuery();

  // google ads
  const [googleAds] = useLazyGoogleAdsQuery();
  const [googleAdsVerify] = useLazyVerifyGoogleAdsTokenQuery();
  const [removeGoogleAds] = useLazyRemoveGoogleAdsQuery();
  const [deleteGoogleAdsIntegration] = useDeleteGoogleAdsIntegrationMutation();
  const [listGoogleAds] = useLazyGetFirstGoogleAdsDataQuery();

  // youtube integration
  const [youtubeIntegration] = useLazyYoutubeIntegrationQuery();
  const [youtubeVerify] = useLazyVerifyYoutubeTokenQuery()
  const [removeYoutube] = useLazyRemoveYoutubeQuery();
  const [deleteYoutubeIntegration] = useDeleteYoutubeIntegrationMutation();
  const [listYoutube] = useLazyListYoutubeQuery();

  // bottom modal expansion
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAllConnectedTo, setShowAllConnectedTo] = useState(false);

  const handleIntegrationData = useCallback(() => {
    const integrationTitle = (type) => {
      switch (type) {
        case "googleanalytics": return "GA4";
        case "shopify": return "Shopify";
        case "meta": return "Meta";
        case "googleads": return "GoogleAds";
        case "custom": return "Custom BigQuery";
        default: return type;
      }
    };

    const integrationData = detailsOverlayModalData?.data;

    if (detailsOverlayModalData) {
      const IconComponent = iconMap[integrationData.icon]
        || (integrationData.name === 'custom-Magento' ? BrandMagento
          : integrationData.name === 'custom-Shopify' ? BrandShopify
            : integrationData.name === 'custom-Meta' ? BrandMeta
              : null);

      const connectedProps = listAnalyticsData?.property_names
        ?.filter(p => p.integrationType === integrationTitle(integrationData?.name))
        ?.map(p => p.propertyName || p.propertyId);

      return {
        icon: IconComponent && (
          <div className="flex items-center">
            <IconComponent className="w-16 h-auto" />
          </div>
        ),
        type: integrationData?.type,
        overLayIcon: IconComponent && (
          <div className="inline-flex rounded-md items-center size-fit border border-gray-300 bg-white">
            <IconComponent className="w-28 h-28" />
          </div>
        ),
        tool: integrationTitle(integrationData?.name),
        connectedTo: connectedProps ?? [],
        syncHistory: integrationData?.syncHistory,
        lastSynced: integrationData?.syncHistory?.lastSynced &&
          new Date(integrationData?.syncHistory?.lastSynced).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          }),
        displayName: integrationData?.displayName,
        aboutOauth: integrationData?.aboutOauth,
        integrationName: integrationData?.name?.toLowerCase(),
        metaExpiration: integrationData?.name === 'meta' ? getStatus?.meta?.meta_expiration : null,
      };
    }
  }, [detailsOverlayModalData, iconMap, listAnalyticsData, getStatus]);

  const [selectedTab, setSelectedTab] = useState('about');

  const handleTabChange = (value) => {
    if (value === 'manage_properties') {
      toast.info('This feature is coming soon!');
      return; // Don't change the tab
    }
    setSelectedTab(value);
  };

  const integrationData = handleIntegrationData();
  const {
    data: allToolsAccounts,
    isLoading: isLoadingToolsAccounts,
    error: toolsAccountsError
  } = useGetAllToolsAccountsForReselectQuery(
    { tool: integrationData?.tool },
    {
      skip: true // Skip this query since the feature is coming soon
    }
  );

  const mappedAccounts = useMemo(() => {
    const connected = integrationData?.connectedTo ?? [];
    return allToolsAccounts?.map(acc => ({
      label: acc.account_name,
      value: acc.account_name,
      connected: connected.includes(acc.account_name),
      disabled: connected.includes(acc.account_name), // Disable already connected accounts
    })) || [];
  }, [allToolsAccounts, integrationData]);

  const [selectedProperties, setSelectedProperties] = useState([]);

  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get("code");
  console.log("code = ", code)
  const url = window.location.href;
  console.log('url = ', url)
  const isIntegrationPage = url.includes("/integrations") || url.includes("basepath=integrations");
  console.log("isIntegrationPage", isIntegrationPage)
  const shopifyCode = {
    token,
    code: urlParams.get("code"),
    hmac: urlParams.get("hmac"),
    shop: urlParams.get("shop"),
    state: urlParams.get("state"),
    basePath: urlParams.get("basePath")
  };

  useEffect(() => {
    console.log('XX: code', code, 'oauthProcessed:', oauthProcessed)
    if (code && !oauthProcessed) {
      setOauthProcessed(true);
      oauthOpenModal('accessGranted');
      handleSubmitApprove();
    }
  }, [code, oauthProcessed]);

  // Cleanup URL parameters after successful OAuth processing
  useEffect(() => {
    if (oauthProcessed && code) {
      const timer = setTimeout(() => {
        const url = new URL(window.location.href);
        url.searchParams.delete('code');
        url.searchParams.delete('state');
        url.searchParams.delete('hmac');
        url.searchParams.delete('shop');
        window.history.replaceState({}, '', url.toString());
        setOauthProcessed(false);
      }, 3000); // Clean up after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [oauthProcessed, code]);

  console.log("isOauthOpen", isOauthOpen);
  console.log("Component state:", {
    loading,
    oauthProcessed,
    isLoadingStatus,
    code: !!code,
    shopifyTimerActive: !!shopifyTimerRef.current
  });

  // Refetch integration status when workspace changes
  useEffect(() => {
    if (workspace_id) {
      console.log('Workspace changed, refetching integration status:', workspace_id);
      refetch();
    }
  }, [workspace_id, refetch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (shopifyTimerRef.current) {
        clearInterval(shopifyTimerRef.current);
        shopifyTimerRef.current = null;
      }
      setOauthProcessed(false);
      setLoading(false);
    };
  }, []);



  const handleAdd = async (service) => {
    const { name } = service;
    // console.log(`Add ${name} integration button is clicked.`);
    if (name === 'googleanalytics') {
      try {
        const payload = {
          token,
          basepath: isIntegrationPage ? 'integrations' : (signup ? 'set-up-connection' : 'onboardingintegration'),
          workspace_object_id: getCurrentWorkspaceObjectId()
        }
        const response = await lazyIntegration(payload);
        // Redirect manually based on the `Location`
        if (response && response?.data) {
          window.location.href = response?.data?.authorization_url;
          refetch();
        } else if (response?.error) {
          toast.error(response?.error?.data?.message || 'Unexpected error');
        }
      } catch (error) {
        console.error('Error:', error);
      }
    }
    if (name === 'shopify') {
      openShopifyModal('shopify');
    }
    if (name === 'meta') {
      try {
        const payload = {
          basepath: isIntegrationPage ? 'integrations' : (signup ? 'set-up-connection' : 'onboardingintegration'),
          workspace_object_id: getCurrentWorkspaceObjectId()
        }
        const response = await metaIntegration(payload);
        // Redirect manually based on the `Location`
        if (response && response?.data) {
          window.location.href = response?.data;
          refetch();
        } else if (response?.error) {
          toast.error(response?.error?.data?.message || 'Unexpected error');
        }
      } catch (error) {
        console.error('Error:', error);
      }
    }
    if (name === 'googleads') {
      try {
        const payload = {
          basepath: isIntegrationPage ? 'integrations' : (signup ? 'set-up-connection' : 'onboardingintegration'),
          workspace_object_id: getCurrentWorkspaceObjectId()
        }
        const response = await googleAds(payload);
        // Redirect manually based on the `Location`
        if (response && response?.data) {
          window.location.href = response?.data;
          refetch();
        } else if (response?.error) {
          toast.error(response?.error?.data?.message || 'Unexpected error');
        }
      } catch (error) {
        console.error('Error:', error);
      }
    }
    if (name === 'youtube') {
      try {
        const payload = {
          token,
          basepath: isIntegrationPage ? 'integrations' : (signup ? 'set-up-connection' : 'onboardingintegration')

        }
        const response = await youtubeIntegration(payload);

        // Redirect manually based on the `Location`
        if (response && response?.data) {
          window.location.href = response?.data?.authorization_url;
          refetch();
        } else if (response?.error) {
          toast.error(response?.error?.data?.message || 'Unexpected error');
        }
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const handleRemove = async () => {

    const serviceName = deleteModalData || handleIntegrationData()?.integrationName;
    if (!serviceName) {
      toast.error('Service name not found');
      return;
    }

    if (serviceName === 'googleanalytics') {
      deleteGAIntegration()
        .unwrap()
        .then((response) => {
          refetch();
          toast.success(response?.message || 'Account removed successfully');
          closeDeleteModal();
          closeDetailsOverlayModal();
        })
        .catch((error) => {
          toast.error(error?.data?.error || 'Unexpected error');
          closeDeleteModal();
        });
    }
    if (serviceName === 'shopify') {
      deleteShopifyIntegration()
        .unwrap()
        .then((response) => {
          refetch();
          toast.success(response?.message || 'Account removed successfully');
          closeDeleteModal();
          closeDetailsOverlayModal();
        })
        .catch((error) => {
          toast.error(error?.data?.error || 'Unexpected error');
          closeDeleteModal();
        });
    }
    if (serviceName === 'meta') {
      deleteMetaIntegration()
        .unwrap()
        .then((response) => {
          refetch();
          toast.success(response?.message || 'Account removed successfully');
          closeDeleteModal();
          closeDetailsOverlayModal();
        })
        .catch((error) => {
          toast.error(error?.data?.error || 'Unexpected error');
          closeDeleteModal();
        })
    }
    if (serviceName === 'googleads') {
      deleteGoogleAdsIntegration()
        .unwrap()
        .then((response) => {
          refetch();
          toast.success(response?.message || 'Account removed successfully');
          closeDeleteModal();
          closeDetailsOverlayModal();
        })
        .catch((error) => {
          toast.error(error?.data?.error || 'Unexpected error');
          closeDeleteModal();
        })
    }
    if (serviceName === 'youtube') {
      deleteYoutubeIntegration()
        .unwrap()
        .then((response) => {
          refetch();
          toast.success(response?.message || 'Account removed successfully');
          closeDeleteModal();
          closeDetailsOverlayModal();
        })
        .catch((error) => {
          toast.error(error?.data?.error || 'Unexpected error');
          closeDeleteModal();
        });
    }
  };

  const handleShopify = async () => {
    try {
      const shopUrl = getValues('shopUrl');
      const shopAccessToken = getValues('shopAccessToken');
      if (!shopUrl) return setError('shopUrl', { message: 'Shop URL is required' });
      if (!shopAccessToken) return setError('shopAccessToken', { message: 'Access Token is required' });
      let payload = {
        token: token,
        data: {
          shop: shopUrl,
          access_token: shopAccessToken,
        },
        basepath: isIntegrationPage ? 'integrations' : (signup ? 'set-up-connection' : 'onboardingintegration')
        ,
      }
      const response = await integrateShopify(payload);
      if (response && response?.data) {
        // window.location.href = response?.data;
        refetch();
        closeShopifyModal();
      } else if (response?.error) {
        throw new Error(response?.error?.data?.message || 'Unexpected error');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message);
    }
  }

  const handleSubmitApprove = () => {
    setLoading(true)
    console.log('location.pathname', location.pathname)
    if (location.pathname.includes('oauth/google') && !location.pathname.includes('oauth/google-ads')) {
      const state = urlParams.get("state");
      const payload = {
        basepath: isIntegrationPage ? 'integrations' : (signup ? 'set-up-connection' : 'onboardingintegration'),
        code: code,
        state: state
      }
      gooleVerify(payload)
        .unwrap()
        .then((res) => {
          setLoading(false)
          oauthCloseModal();
          if (res) {
            getIntegrationAccountNames()
              .unwrap()
              .then((response) => {
                if (response) {
                  let temp = response?.accounts.map((item) => ({ label: maskName(item?.account_name), value: item?.account_name }))
                  setAccountOptions(temp)
                }
              })
              .catch((error) => console.error('Error:', error));
            openAddAccountToWorkspaceModal('googleanalytics');
            refetch();
            // eventData()
            //   .unwrap()
            //   .then((response) => {
            //     if (response?.error) {
            //     }
            //   })
            //   .catch((error) => console.error('Error:', error));
            toast.success('Account added successfully');
          }
        })
        .catch((error) => {
          toast.error(error?.data?.error || 'Unexpected error');
          oauthCloseModal();
          if (isIntegrationPage) {
            navigate('/integrations');
          } else {
            navigate(signup ? '/set-up-connection' : "/integrations")
          }
        })
        .finally(() => {
          setLoading(false)
          oauthCloseModal();
          setOauthProcessed(false);
          // Clear URL parameters to prevent re-processing
          const url = new URL(window.location.href);
          url.searchParams.delete('code');
          url.searchParams.delete('state');
          window.history.replaceState({}, '', url.toString());
        })
    }

    if (location.pathname.includes('shopify/auth')) {
      shopifyVerify(shopifyCode)
        .unwrap()
        .then((response) => {
          setLoading(false)
          oauthCloseModal();
          if (response) {
            refetch();
            toast.success('Account added successfully');
          }
          if (isIntegrationPage) {
            navigate('/integrations');
          } else {
            navigate(signup ? '/set-up-connection' : "/integrations")
          }

        })
        .catch((error) => {
          toast.error(error?.data?.error || 'Unexpected error');
        })
        .finally(() => {
          setLoading(false)
          oauthCloseModal();
          if (isIntegrationPage) {
            navigate('/integrations');
          } else {
            navigate(signup ? '/set-up-connection' : "/integrations")
          }
        })
    }

    if (location.pathname.includes('oauth/google-ads')) {
      const state = urlParams.get("state");
      const payload = {
        basepath: isIntegrationPage ? 'integrations' : (signup ? 'set-up-connection' : 'onboardingintegration'),
        code: code,
        state: state
      }
      googleAdsVerify(payload)
        .unwrap()
        .then((res) => {
          setLoading(false)
          oauthCloseModal();
          if (res) {
            getGoogleAdsAccountNames()
              .unwrap()
              .then((response) => {
                if (response) {
                  let tempVal = Object.entries(response?.accounts).map(([key, value]) => ({ label: maskName(value?.name), value: key }));
                  setAccountOptions(tempVal)
                }
              })
              // 
              .catch((error) => console.error('Error:', error));
            openAddAccountToWorkspaceModal('googleads');
            refetch();
            toast.success('Account added successfully');
          }
        })
        .catch((error) => {
          toast.error(error?.data?.error || 'Unexpected error');
          oauthCloseModal();
          if (isIntegrationPage) {
            navigate('/integrations');
          } else {
            navigate(signup ? '/set-up-connection' : "/integrations")
          }
        })
        .finally(() => {
          setLoading(false)
          oauthCloseModal();
        })
    }

    if (location.pathname.includes('oauth/youtube')) {
      const state = urlParams.get("state");
      const payload = {
        basepath: isIntegrationPage ? 'integrations' : (signup ? 'set-up-connection' : 'onboardingintegration'),
        code: code,
        state: state
      }
      youtubeVerify(payload)
        .unwrap()
        .then((response) => {
          setLoading(false)
          oauthCloseModal();
          if (response) {
            refetch();
            listYoutube()
              .unwrap()
              .then((reponse) => {
                if (isIntegrationPage) {
                  navigate('/integrations');
                } else {
                  navigate(signup ? '/set-up-connection' : "/integrations")
                }
                if (reponse?.error) {
                  throw new Error(reponse?.error || 'Unexpected error');
                }
              })
              .catch((error) => console.error('Error:', error));
            toast.success('Account added successfully');
          }
        })
        .catch((error) => {
          toast.error(error?.data?.error || 'Unexpected error');
        })
        .finally(() => {
          setLoading(false)
          oauthCloseModal();
        })
    }

    if (location.pathname.includes('meta/auth')) {
      const payload = {
        basepath: isIntegrationPage ? 'integrations' : (signup ? 'set-up-connection' : 'onboardingintegration')
        ,
        code: code
      }
      metaVerify(payload)
        .unwrap()
        .then((response) => {
          setLoading(false)
          if (response) {
            getMetaAccounts()
              .unwrap()
              .then((reponse) => {
                if (reponse?.error) {
                  // getMetaAccountsAd()
                  //   .unwrap()
                  //   .then((reponse) => {
                  //     if (reponse?.error) {
                  //       throw new Error(reponse?.error || 'Unexpected error');
                  //     }
                  //   })
                  throw new Error(reponse?.error || 'Unexpected error');
                }
              })
              .catch((error) => console.error('Error:', error));
            toast.success('Account added successfully');
          }
        })
        .catch((error) => {
          toast.error(error?.data?.error || 'Unexpected error');
        })
        .finally(() => {
          setLoading(false);
          oauthCloseModal();
          if (isIntegrationPage) {
            navigate('/integrations');
          } else {
            navigate(signup ? '/set-up-connection' : "/integrations")
          }
        })
    }
  }

  const handleSubmitGaRedirect = (data) => {
    if (location.pathname.includes('/oauth/google-ads')) {
      let payload = {
        customer_ids: data.accounts
      }
      addGoogleAdsAccount(payload)
        .unwrap()
        .then((response) => {
          if (response) {
            toast.success(response?.message || 'Workspace added successfully');
            listGoogleAds()
              .unwrap()
              .then(() => { })
              .catch((error) => console.error('Error:', error));
            if (isIntegrationPage) {
              navigate('/conversation');
            } else {
              navigate(signup ? '/set-up-connection' : "/integrations")
            }
          }
        })
        .catch((error) => {
          toast.error(error?.data?.error || 'Unexpected error');
        })
    } else if (location.pathname.includes('/oauth/google')) {
      const payload = {
        data: {
          ga_account_id: data.accounts
        },
        workspace_id: workspace_id
      }
      addGaAccount(payload)
        .unwrap()
        .then((response) => {
          if (response) {
            toast.success(response?.message || 'Workspace added successfully');
            closeAddAccountToWorkspaceModal();
            if (isIntegrationPage) {
              navigate('/conversation');
            } else {
              navigate(signup ? '/set-up-connection' : "/integrations")
            }
          }
        })
        .catch((error) => {
          toast.error(error?.data?.error || 'Unexpected error');
          // handleCancel();
        });
    }
  }

  const tabContentRef = useRef(null);
  const tabContainerRef = useRef(null);

  const handleDetailsModal = (service) => {
    const { name } = service;
    if (isIntegrationPage) {
      openDetailsOverlayModal(name, integrations.find((integration) => integration.name === name));
    }
  };

  // Animate height on tab change
  useEffect(() => {
    if (!tabContainerRef.current || !tabContentRef.current) return;

    const container = tabContainerRef.current;
    const content = tabContentRef.current;

    // Get the current height
    const startHeight = container.offsetHeight;

    // Temporarily set height to auto to measure new content
    container.style.height = 'auto';
    const endHeight = content.offsetHeight;

    // Reset to start height for animation
    container.style.height = `${startHeight}px`;

    // Force reflow to ensure the browser registers the height change
    void container.offsetHeight;

    // Animate to end height
    requestAnimationFrame(() => {
      gsap.to(container, {
        height: endHeight,
        duration: 0.3,
        ease: 'power2.inOut',
        onComplete: () => {
          container.style.height = 'auto';
        }
      });
    });
  }, [selectedTab]);

  return (
    <>
      <div className={className ? className : `grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4`} style={style} data-tour="integration-cards">
        {integrations.map((integrate) => {
          const IconComponent = iconMap[integrate.icon]; // Get the correct icon component
          if (integrate.name === 'youtube') return null;
          return (
            <Cards
              key={integrate.name}
              data-tour={`${integrate.name}-card`}
              classname={`p-4 gap-3 ${!integrate.isUpgraded && "border-0 bg-black-100"} ${getStatus && getStatus[integrate?.name] ? "cursor-pointer" : "cursor-default"}`}
              variant="secondary"
              childClassName="flex-col items-start gap-4"
              onClick={() => {
                if (getStatus && getStatus[integrate?.name]) {
                  handleDetailsModal(integrate);
                }
              }}
            >
              <div className={'flex justify-start items-center gap-1'}>
                {/* {React.createElement(integrate.icon, { className: "w-12 h-auto" })} */}
                {IconComponent && <IconComponent className="w-12 h-auto" />}
                <h5 className="font-medium text-text-700 font-albert">{integrate.displayName}</h5>
              </div>
              <div className="flex w-full gap-2 justify-between items-center">
                <Button
                  data-tour="connect-button"
                  type={getStatus && getStatus[integrate?.name] ? "primary" : "default"}
                  icon={
                    getStatus && getStatus[integrate?.name] && <CheckIcon
                      className={"h-3.5 w-3.5"}
                    />
                  }
                  loading={isLoadingStatus}
                  disabled={error?.data?.message || isLoadingStatus || (ownUser && integrate.name === 'shopify') || (ownUser && integrate.name === 'googleads')}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (getStatus && !getStatus[integrate?.name]) {
                      handleAdd(integrate)
                    }
                    if (!isIntegrationPage && getStatus && getStatus[integrate?.name]) {
                      openDeleteModal(integrate?.name);
                    } else if (isIntegrationPage && getStatus && getStatus[integrate?.name]) {
                      handleDetailsModal(integrate);
                    }
                  }
                  }
                >
                  {getStatus && getStatus[integrate?.name] ? "Added" : `Add to ${isInIframe}`}
                </Button>
                {getStatus && getStatus[integrate?.name] && (
                  integrate?.name === 'meta' && getStatus?.meta?.meta_expiration && (
                    <AntBadge
                      count={`Expires in ${getStatus.meta.meta_expiration.days_remaining} days`}
                      style={{
                        backgroundColor:
                          getStatus.meta.meta_expiration.days_remaining <= 7 ? '#dc2626' :
                            getStatus.meta.meta_expiration.days_remaining <= 30 ? '#ea580c' :
                              getStatus.meta.meta_expiration.days_remaining <= 45 ? '#eab308' :
                                '#16a34a',
                        color: 'white',
                        fontWeight: 500,
                        fontSize: '12px',
                        minWidth: '60px',
                        height: '22px',
                        lineHeight: '22px',
                        borderRadius: '11px',
                        padding: '0 8px'
                      }}
                    />
                  )
                )}
              </div>
            </Cards>
          )
        })}
      </div>
      {isShopifyOpen && (
        <Modal
          title="Connect Shopify"
          open={isShopifyOpen}
          onClose={closeShopifyModal}
          className="xl:w-[35%] lg:w-[50%] md:w-[70%] sm:w-[80%]"
        >
          <div className="flex gap-2 items-center">
            <BrandShopify className="w-[40px] h-[40px]" />
            {/* {
              (getUserData?.settings?.email?.endsWith('@oogw.ai') && shopifyModalData === 'shopify') ? */}
            {/* <h4>Enter your Shopify shop URL</h4> : */}
            <div>
              <h4>Shopify Integration</h4>
              <p className="text-sm">
                Redirecting to{' '}
                <a
                  href="https://apps.shopify.com/oogwai-analytics"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-500 hover:underline"
                  onClick={() => {
                    if (shopifyTimerRef.current) {
                      clearInterval(shopifyTimerRef.current);
                      shopifyTimerRef.current = null;
                    }
                    closeShopifyModal();
                  }}
                >
                  apps.shopify.com/oogwai-analytics
                </a>
                {' '}in {shopifyTimer > 0 ? shopifyTimer : 0} seconds
              </p>
            </div>
            {/* } */}
          </div>
          {/* {
            (getUserData?.settings?.email?.endsWith('@oogw.ai') && shopifyModalData === 'shopify') && (
              <div className="flex flex-col gap-4 mt-5" onClick={(e) => e.stopPropagation()}>
                <InputField
                  label="Shop URL"
                  name="shopUrl"
                  control={control}
                  placeholder="example.myshopify.com"
                  onClick={(e) => e.stopPropagation()}
                />
                <InputField
                  label="Access Token"
                  name="shopAccessToken"
                  control={control}
                  placeholder="Enter your access token"
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="flex justify-end space-x-4 mt-1">
                  <Button
                    type="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShopify()
                    }}
                    loading={loading}
                  >
                    Add to Oogwai
                  </Button>
                </div>
              </div>
            )
          } */}
        </Modal>
      )}

      {isDetailsOverlayOpen &&
        (
          <ModalBottomOverlay
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
            header={
              <div className="flex justify-between">
                <div className="flex gap-4">
                  {handleIntegrationData()?.overLayIcon}
                  <div className="flex flex-col justify-start">
                    <div>
                      <h4>{handleIntegrationData()?.displayName}</h4>
                      <h5 className="font-normal">Instantly get automated reports and alerts</h5>
                    </div>
                    <p className="font-normal">Connected to:</p>
                    <div
                      className="overflow-hidden transition-all duration-300 ease-in-out"
                      style={{
                        maxHeight: showAllConnectedTo ? '500px' : '80px'
                      }}
                    >
                      <div className="flex flex-wrap gap-1 py-0.5 items-center">
                        {(() => {
                          const connectedItems = handleIntegrationData()?.connectedTo || [];
                          const maxItemsToShow = 8;
                          const hasMore = connectedItems.length > maxItemsToShow;

                          return (
                            <>
                              {connectedItems.map((item, index) => (
                                <div
                                  key={item}
                                  className="bg-gray-200 border border-gray-300 px-2 py-1 rounded-full text-xs transition-all duration-300"
                                  style={{
                                    opacity: !showAllConnectedTo && index >= maxItemsToShow ? 0 : 1,
                                    transform: !showAllConnectedTo && index >= maxItemsToShow ? 'scale(0.8)' : 'scale(1)',
                                    display: !showAllConnectedTo && index >= maxItemsToShow ? 'none' : 'inline-block'
                                  }}
                                >
                                  {item}
                                </div>
                              ))}
                              {hasMore && (
                                <button
                                  onClick={() => setShowAllConnectedTo(!showAllConnectedTo)}
                                  className="text-xs text-blue-600 hover:text-blue-700 underline ml-1 transition-colors duration-200"
                                >
                                  {showAllConnectedTo ? 'View Less' : `+${connectedItems.length - maxItemsToShow} more`}
                                </button>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    {/* <p>Last Sync: {handleIntegrationData()?.lastSynced}</p> */}
                    {/* <Button
                      color="primary"
                      className="w-fit"
                      onClick={() => setIsExpanded(!isExpanded)}
                      variant="text" icon={<ChevronDownIcon className={isExpanded ? "rotate-180" : ""} />}>
                      {isExpanded ? "Hide Sync History" : "View Sync History"}
                    </Button> */}
                  </div>
                </div>
                <div className="flex lg:gap-2.5 flex-col lg:flex-row gap-2 items-start lg:items-end">
                  <Button
                    danger
                    icon={<CloseIcon className="w-2.5 h-2.5" />}
                    onClick={() => {
                      openDeleteModal(handleIntegrationData()?.integrationName);
                    }}
                  >
                    Remove
                  </Button>
                  {handleIntegrationData()?.integrationName === "googleanalytics" && (
                    <Button
                      type="default"
                      icon={<InfoSettings className="w-5 h-5" />}
                      onClick={() => navigate(`/integrations/custom-events-data/${handleIntegrationData()?.integrationName}`)}>
                      Map Custom Events
                    </Button>
                  )}
                  <Button type="primary" iconPosition="end" icon={<ExternalLinkIcon className="w-5 h-5" />}
                    onClick={() => {
                      navigate(`/integrations/view-raw-data/${handleIntegrationData()?.integrationName}`);
                    }}>
                    {handleIntegrationData()?.integrationName?.includes("custom") ? "View Schema" : "View Raw Data"}
                  </Button>
                </div>

              </div>
            }
            onClose={() => {
              setSelectedTab('about');
              closeDetailsOverlayModal();
            }}
            onSave={() => {
              setSelectedTab('about');
              closeDetailsOverlayModal();
            }}
          >
            <div className="content-feature p-4 grid grid-cols-1">
              <CustomSegmented
                size='large'
                variant='squareGroup'
                value={selectedTab}
                className='w-fit'
                onChange={handleTabChange}
                options={[
                  { label: `About ${handleIntegrationData()?.displayName}`, value: 'about' },
                  { label: 'Manage Properties', value: 'manage_properties' },
                ]}
                control={control}
              />
              <div
                ref={tabContainerRef}
                className="overflow-hidden transition-all"
                style={{ minHeight: '100px' }}
              >
                <div ref={tabContentRef}>
                  {selectedTab === 'about' && (
                    <div className="p-4 space-y-4 divide-y divide-gray-300">
                      <div>
                        {/* <h4>About {handleIntegrationData()?.displayName}</h4> */}
                        <p className="font-normal">We'll alert you via <span className="font-medium">{userEmail}</span> if there is any unusual activity on your account.</p>
                      </div>

                      {/* Meta Token Expiration Section */}
                      {handleIntegrationData()?.metaExpiration && (
                        <div className="pt-4">
                          <h4 className="text-base font-semibold mb-3">Token Expiration Status</h4>
                          <div className={`p-4 rounded-lg border-2 ${handleIntegrationData().metaExpiration.days_remaining <= 7 ? 'bg-red-50 border-red-200' :
                              handleIntegrationData().metaExpiration.days_remaining <= 30 ? 'bg-orange-50 border-orange-200' :
                                handleIntegrationData().metaExpiration.days_remaining <= 45 ? 'bg-yellow-50 border-yellow-200' :
                                  'bg-green-50 border-green-200'
                            }`}>
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <span className="text-lg">
                                  {handleIntegrationData().metaExpiration.days_remaining <= 7 ? '⚠️' :
                                    handleIntegrationData().metaExpiration.days_remaining <= 30 ? '⏰' :
                                      'ℹ️'}
                                </span>
                                <span className="font-medium">
                                  {handleIntegrationData().metaExpiration.message}
                                </span>
                              </div>
                              <Badge
                                variant={
                                  handleIntegrationData().metaExpiration.days_remaining <= 7 ? 'red' :
                                    handleIntegrationData().metaExpiration.days_remaining <= 30 ? 'orange' :
                                      handleIntegrationData().metaExpiration.days_remaining <= 45 ? 'yellow' :
                                        'green'
                                }
                                size="md"
                                rounded
                              >
                                {handleIntegrationData().metaExpiration.days_remaining} days
                              </Badge>
                            </div>

                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">Status:</span>
                              <Badge
                                variant={handleIntegrationData().metaExpiration.status === 'active' ? 'green' : 'red'}
                                size="sm"
                              >
                                {handleIntegrationData().metaExpiration.status}
                              </Badge>
                            </div>

                            {handleIntegrationData().metaExpiration.days_remaining <= 7 && (
                              <div className="mt-3 p-2 bg-red-100 rounded-md">
                                <p className="text-xs text-red-800 font-medium">
                                  ⚠️ Critical: Your Meta integration will stop working soon
                                </p>
                              </div>
                            )}

                            {/* <div className="mt-4 bg-gray-50 rounded-md p-3">
                              <h5 className="text-sm font-medium text-gray-900 mb-2">What happens when the token expires?</h5>
                              <ul className="text-xs text-gray-600 space-y-1 list-disc ml-4">
                                <li>Data synchronization will stop</li>
                                <li>Analytics reports won't be updated</li>
                                <li>Campaign tracking will be paused</li>
                                <li>You'll need to re-authenticate to continue</li>
                              </ul>
                            </div>
                            
                            <div className="mt-4 flex gap-2">
                              <Button 
                                type="primary" 
                                onClick={() => {
                                  // Handle token refresh
                                  toast.info('Redirecting to Meta authentication...');
                                  // Add your refresh token logic here
                                }}
                                className="flex-1"
                              >
                                Refresh Token Now
                              </Button>
                            </div> */}
                          </div>
                        </div>
                      )}

                      <div className="pt-4 px-4">
                        <ul className="list-disc ml-2 text-sm text-gray-900">
                          {handleIntegrationData()?.aboutOauth?.length > 0 ? (
                            handleIntegrationData().aboutOauth.map((feature, index) => (
                              <li key={index}>{feature}</li>
                            ))
                          ) : (
                            <li>No features available</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  )}
                  {selectedTab === 'manage_properties' && (
                    <div className="p-4 space-y-4">
                      <h4 className="text-base font-medium">Properties</h4>
                      <p className="text-sm text-gray-500 mb-4">
                        Manage your connected accounts for {integrationData?.displayName}
                      </p>

                      <div className="space-y-4">
                        <div>
                          <h5 className="text-sm font-medium mb-2">Connected Accounts</h5>
                          {integrationData?.connectedTo?.length > 0 ? (
                            <>
                              <div
                                className="overflow-hidden transition-all duration-300 ease-in-out"
                                style={{
                                  maxHeight: showAllConnectedTo ? '600px' : '180px'
                                }}
                              >
                                <div className="grid grid-cols-2 gap-2">
                                  {(() => {
                                    const allAccounts = integrationData?.connectedTo || [];
                                    const maxAccountsToShow = 6;
                                    const hasMore = allAccounts.length > maxAccountsToShow;

                                    return allAccounts.map((item, index) => (
                                      <div
                                        key={index}
                                        className="transition-all duration-300"
                                        style={{
                                          opacity: !showAllConnectedTo && index >= maxAccountsToShow ? 0 : 1,
                                          transform: !showAllConnectedTo && index >= maxAccountsToShow ? 'translateY(-10px)' : 'translateY(0)',
                                          display: !showAllConnectedTo && index >= maxAccountsToShow ? 'none' : 'block'
                                        }}
                                      >
                                        <Checkbox
                                          // checked={true}
                                          disabled={false}
                                          className="connected-checkbox"
                                        >
                                          <span className="text-sm">{item}</span>
                                        </Checkbox>
                                      </div>
                                    ));
                                  })()}
                                </div>
                              </div>
                              {integrationData?.connectedTo?.length > 6 && (
                                <button
                                  onClick={() => setShowAllConnectedTo(!showAllConnectedTo)}
                                  className="mt-2 text-sm text-blue-600 hover:text-blue-700 underline transition-colors duration-200"
                                >
                                  {showAllConnectedTo
                                    ? 'Show Less'
                                    : `View ${integrationData.connectedTo.length - 6} More`
                                  }
                                </button>
                              )}
                            </>
                          ) : (
                            <p className="text-sm text-gray-500">No accounts connected yet</p>
                          )}
                        </div>

                        <div>
                          <h5 className="text-sm font-medium mb-2">Available Accounts</h5>
                          {isLoadingToolsAccounts ? (
                            <div className="flex items-center justify-center py-4">
                              <Spin size="small" />
                              <span className="ml-2 text-sm text-gray-500">Loading available accounts...</span>
                            </div>
                          ) : toolsAccountsError ? (
                            <div className="bg-red-50 border border-red-200 rounded-md p-3">
                              <p className="text-sm text-red-600">
                                Failed to load available accounts. Please try again later.
                              </p>
                              {toolsAccountsError?.data?.message && (
                                <p className="text-xs text-red-500 mt-1">{toolsAccountsError.data.message}</p>
                              )}
                            </div>
                          ) : allToolsAccounts && allToolsAccounts.length > 0 ? (
                            <>
                              <Checkbox.Group
                                value={selectedProperties}
                                onChange={setSelectedProperties}
                                className="grid grid-cols-2 gap-2"
                              >
                                {mappedAccounts
                                  .filter(acc => !acc.connected)
                                  .map((acc, index) => (
                                    <Checkbox key={index} value={acc.value}>
                                      {acc.label}
                                    </Checkbox>
                                  ))}
                              </Checkbox.Group>
                              {mappedAccounts.filter(acc => !acc.connected).length === 0 && (
                                <p className="text-sm text-gray-500">All available accounts are already connected</p>
                              )}
                            </>
                          ) : (
                            <p className="text-sm text-gray-500">No available accounts to add</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* <div className="content-container border-t px-10 p-4 border-gray-300 grid grid-cols-1 divide-y space-y-1 divide-gray-300">
              <div className="pb-3">
                <h4>Sync History</h4>
                <p>We’ll alert you via <span className="font-medium">{userEmail}</span> if there is any unusual activity on your account.</p>
              </div>
              {handleIntegrationData()?.syncHistory?.data?.length > 0 ? (
                handleIntegrationData()?.syncHistory?.data.map((entry, index) => (
                  <div key={index} className="flex gap-3 items-center py-5">
                    <p className="font-medium">{new Date(entry.date)
                      .toLocaleString('en-US',
                        {
                          year: 'numeric',
                          month: 'short',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        }).replace(',', ' at')}
                    </p>
                    {index === 0 && <Badge title='Last Sync' variant='active' />}
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500  py-5">No sync history available</p>
              )}
            </div> */}
          </ModalBottomOverlay>
        )}

      {isDeleteOpen && (
        <Modal
          onClose={() => {
            closeDeleteModal();
          }}
          onSave={() => {
            handleRemove();
          }}
          className="xl:max-w-[60%] lg:max-w-[70%] md:max-w-[75%]"
        >
          <div className="flex flex-row items-start gap-5">
            <IconRings numRings={5} ringBaseSize={48} ringSpacing={56} className=" -z-10">
              <div className="bg-[#FEE4E2] p-3 w-fit h-fit rounded-full">
                <TrashIcon className="text-[#D92D20] w-5 h-5" />
              </div>
            </IconRings>
            {/* <div className="bg-[#FEE4E2] p-3 h-fit rounded-full">
              <TrashIcon className="text-[#D92D20] w-5 h-5" />
            </div> */}
            <div className="z-10">
              <h4 className=" text-nowrap">Delete Integration</h4>
              <p className="text-sm">Are you sure you want to delete this integration? This action cannot be undone.</p>
            </div>
          </div>
          <div className="flex flex-row justify-between items-center pt-10">
            <Checkbox
              control={control}
              label={'Don’t show again'}
              name="dontShowAgain"
              rules={{
                required: false,
              }}
            />
            <div className="space-x-4">
              <Button
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent backdrop closure
                  closeDeleteModal();
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent backdrop closure
                  handleRemove();
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {isOauthOpen && (
        <Modal
          defaultClose={false}
          // onClose={() => {
          //   oauthCloseModal();
          //   navigate("/integrations")
          // }}
          timer={2000}
          className="md:min-w-fit"
          onSave={() => console.log('Modal auto-closed')}
        >
          <div className="flex flex-col gap-2">
            <h5 className="text-xl text-center text-text-700 text-nowrap">You have given permission to access Oogwai</h5>
          </div>
        </Modal>
      )}

      <AntdModal defaultClose={false} centered className="xl:max-w-[40%] lg:max-w-[50%] md:max-w-[65%] ga_redirect_modal" open={isAddAccountToWorkspaceOpen}>
        <div className="p-4">
          <div className="pt-2">
            <div className="flex gap-2">
              {isGoogleAds ? <BrandGAds className="h-[30px] w-[30px]" /> : <BrandGAnalytics className="h-[30px] w-[30px]" />}
              <h4 className="">Select Account to Add</h4>
            </div>
            <p className="text-sm mt-3">You have multiple accounts associated with this email. Please select one to proceed.</p>
          </div>

          <form onSubmit={handleSubmit(handleSubmitGaRedirect)} className="">
            <div className="">
              <Controller
                name={'accounts'}
                control={gaControl}
                rules={{
                  required: 'Account Name is required',
                }}
                error={errors.accounts}
                render={({ field, fieldState: { error: fieldError } }) => (
                  <>
                    {(getIntegrationAccountNamesLoading || getGoogleAdsAccountNamesLoading) && <p className="text-sm mt-2">Fetching accounts...</p>}
                    <Select
                      notFoundContent={(getIntegrationAccountNamesLoading || getGoogleAdsAccountNamesLoading) ? <Spin size="small" /> : 'No accounts found'}
                      onChange={(e) => {
                        field.onChange(e);
                      }}
                      mode='multiple'
                      options={accountOptions?.length > 0 ? accountOptions : []}
                      className="w-full font-semibold text-[#414651]"
                    />
                    {(fieldError || error) && <p className="text-xs tracking-tight leading-5 text-red-500">{typeof error === 'string' ? error : (fieldError || error)?.message}</p>}
                  </>
                )}
              />

            </div>
            <hr />
            <div className="flex items-center justify-end gap-3 mt-3">
              <Button
                onClick={() => {
                  // Trigger the remove integration function
                  const integrationName = isGoogleAds ? 'googleads' : 'googleanalytics';
                  if (integrationName === 'googleanalytics') {
                    deleteGAIntegration()
                      .unwrap()
                      .then((response) => {
                        refetch();
                        toast.success(response?.message || 'Integration cancelled and removed');
                        closeAddAccountToWorkspaceModal();
                        if (isIntegrationPage) {
                          navigate('/integrations');
                        } else {
                          navigate(signup ? '/set-up-connection' : "/integrations")
                        }
                      })
                      .catch((error) => {
                        toast.error(error?.data?.error || 'Failed to cancel integration');
                        closeAddAccountToWorkspaceModal();
                      });
                  } else if (integrationName === 'googleads') {
                    deleteGoogleAdsIntegration()
                      .unwrap()
                      .then((response) => {
                        refetch();
                        toast.success(response?.message || 'Integration cancelled and removed');
                        closeAddAccountToWorkspaceModal();
                        if (isIntegrationPage) {
                          navigate('/integrations');
                        } else {
                          navigate(signup ? '/set-up-connection' : "/integrations")
                        }
                      })
                      .catch((error) => {
                        toast.error(error?.data?.error || 'Failed to cancel integration');
                        closeAddAccountToWorkspaceModal();
                      });
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                className=''
                type="primary"
                htmlType="submit"
              >
                Add to Workspace
              </Button>
            </div>
          </form>

        </div>
      </AntdModal>
    </>
  )
}

export const CustomIntegrationComponent = ({
  signup = false,
  iconMap = {},
  className,
  style,
  control,
  watch,
  reset,
  getValues,
}) => {

  const navigate = useNavigate();

  const { data: getUserData } = useGetGeneralSettingsQuery('');
  const userEmail = getUserData?.settings?.email || 'No email id found';

  const [editable, setEditable] = useState(true);
  const [customName, setCustomName] = useState('');

  const { data: getName } = useGetCustomNameQuery('');
  const [editCustomName, { isLoading: customNameLoading }] = useEditCustomNameMutation();
  const [postSchemaDes, { isLoading: isPostSchemaDesLoading }] = usePostSchemaDesMutation();

  useEffect(() => {
    if (getName?.dataset_ui_name) {
      setCustomName(getName?.dataset_ui_name);
    }
  }, [getName])

  const { data: getStatus, isLoading: isLoadingStatus, refetch, error } = useGetAnalyticStatusQuery();

  const getEmail = localStorage.getItem("oogw.ai_user_email") || userEmail;
  const ownUser = getEmail && getEmail === "demo@oogw.ai" ? true : false;

  const [isExpanded, setIsExpanded] = useState(false);

  // ? MODAL FOR CHECKING SYNC AND DETAILS OF INTEGRATION
  const {
    isOpen: isDetailsOverlayOpen,
    openModal: openDetailsOverlayModal,
    closeModal: closeDetailsOverlayModal,
    modalData: detailsOverlayModalData
  } = useModal();

  // ? MODAL FOR DELETING INTEGRATION
  const {
    isOpen: isDeleteOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
    modalData: deleteModalData
  } = useModal();

  // ? MODAL BIGQUERY INTEGRATION
  const {
    isOpen: isBigQueryIntegrationModalOpen,
    openModal: openBigQueryIntegrationModal,
    closeModal: closeBigQueryIntegrationModal,
    modalData: bigQueryIntegrationModalData
  } = useModal();

  const handleIntegrationData = useCallback(() => {
    if (detailsOverlayModalData) {
      const integrationData = detailsOverlayModalData.data;
      const IconComponent = CustomIntegrationLogo;
      return {
        icon: IconComponent && (
          <div className="flex items-center">
            <IconComponent className="w-16 h-auto" />
          </div>
        ),
        type: integrationData?.type,
        overLayIcon: IconComponent && (
          <div className="inline-flex rounded-md items-center size-fit border border-gray-300 bg-white">
            <IconComponent className="w-28 h-28" />
          </div>
        ),
        syncHistory: integrationData?.syncHistory,
        lastSynced: integrationData?.syncHistory?.lastSynced
          && new Date(integrationData?.syncHistory?.lastSynced).toLocaleString('en-US', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }),
        displayName: integrationData?.displayName,
        aboutOauth: integrationData?.aboutOauth,
        integrationName: integrationData?.name?.toLowerCase(),
      };
    }
  }, [detailsOverlayModalData, iconMap]);

  const handleAdd = async (service) => {
    const { name } = service;

    // BigQuery integration
    if (service.name.includes('custom')) {
      openDetailsOverlayModal(name, { name: name });
    }
  };

  // const [removeCustomIntegrationDataset] = useDeleteCustomIntegrationDatasetIdMutation();
  const [removeBigQuery] = useDeleteCustomBigQueryIntegrationMutation()

  const handleRemove = async (_serviceName) => {
    const serviceName = _serviceName || handleIntegrationData()?.integrationName;
    if (serviceName?.includes('custom-')) {
      const integrationTool = serviceName.includes('shopify')
        ? 'Shopify'
        : serviceName.includes('magento')
          ? 'Magento'
          : serviceName.includes('meta') ? 'Meta' : null;
      // removeCustomIntegrationDataset({ integration_tool: integrationTool })
      removeBigQuery()
        .unwrap()
        .then((response) => {
          toast.success(response?.message || 'Account removed successfully');
          closeDeleteModal();
          closeDetailsOverlayModal();
        })
        .catch((error) => {
          toast.error(error?.data?.error || 'Unexpected error');
          closeDeleteModal();
        });
    }
  };

  return (
    <>
      <div className={className}>
        <Cards
          data-tour="custom-integration-card"
          classname={`p-4`}
          variant="secondary"
          childClassName="h-24 flex-col items-center gap-4 justify-center cursor-pointer"
          onClick={(e) => {
            if (getStatus?.custom_bq) {
              openDetailsOverlayModal("custom-integration", {
                name: 'custom-integration',
                displayName: 'Custom BigQuery',
                type: 'custom',
                syncHistory: getStatus?.custom_bq?.syncHistory || {},
                aboutOauth: [
                  'Connect your custom BigQuery datasets',
                  'Access and analyze your custom data sources',
                  'Set up custom data integrations'
                ]
              });
            }
          }}
        >
          {getStatus?.custom_bq && (
            <div className="relative w-full pointer-events-all mb-2">
              <div className={`flex items-center gap-1.5 rounded-md transition-colors px-2 py-1.5 ${!editable ? 'bg-gray-50 border border-gray-200 hover:border-gray-300' : ''}`}>
                <input
                  type='text'
                  placeholder='Dataset name'
                  className='flex-1 outline-none text-xs font-medium text-gray-900 placeholder:text-gray-400 disabled:text-gray-700 bg-transparent'
                  disabled={editable}
                  value={customName}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setCustomName(e.target.value)}
                />
                {editable ? (
                  <button
                    className="p-1 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditable(false);
                    }}
                    title="Edit"
                  >
                    <EditChatIcon className="w-3 h-3 text-gray-600" />
                  </button>
                ) : (
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button
                      className="p-1 hover:bg-gray-200 rounded transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditable(true);
                      }}
                      title="Cancel"
                    >
                      <CloseIcon className="w-3 h-3 text-gray-500" />
                    </button>
                    <button
                      className="p-1 hover:bg-primary-600 bg-primary-500 rounded-full transition-colors disabled:opacity-50"
                      disabled={customNameLoading}
                      onClick={(e) => {
                        e.stopPropagation();
                        const payload = {
                          dataset_ui_name: customName
                        };
                        editCustomName(payload)
                          .unwrap()
                          .then((response) => {
                            if (response) {
                              toast.success(response?.message || 'Custom name updated successfully');
                              setEditable(true);
                            }
                          })
                          .catch((error) => {
                            toast.error(error?.data?.error || 'Unexpected error');
                            setEditable(false);
                          });
                      }}
                      title="Save"
                    >
                      {customNameLoading ? (
                        <svg className="animate-spin w-3 h-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <CheckIcon className="w-3 h-3 text-white" />
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          <Button
            disabled={ownUser}
            type={getStatus?.custom_bq ? "primary" : "default"}
            icon={getStatus?.custom_bq ? <CheckIcon
              className={"h-3.5 w-3.5"}
            /> : <PlusIcon className="" />}
            onClick={(e) => {
              e.stopPropagation();
              if (getStatus?.custom_bq) {
                openDetailsOverlayModal("custom-integration", {
                  name: 'custom-integration',
                  displayName: 'Custom BigQuery',
                  type: 'custom',
                  syncHistory: getStatus?.custom_bq?.syncHistory || {},
                  aboutOauth: [
                    'Connect your custom BigQuery datasets',
                    'Access and analyze your custom data sources',
                    'Set up custom data integrations'
                  ]
                });
              } else {
                openBigQueryIntegrationModal('bigquery');
              }
            }}
          >
            {getStatus?.custom_bq ? "Added" : "Add another"}
          </Button>
        </Cards>
      </div>
      {isDetailsOverlayOpen &&
        (
          <ModalBottomOverlay
            data-tour="custom-integration-modal"
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
            header={
              <div className="flex justify-between">
                <div className="flex gap-4">
                  {handleIntegrationData()?.overLayIcon}
                  <div>
                    <h4>{handleIntegrationData()?.displayName}</h4>
                    <h5 className="font-normal">Instantly get automated reports and alerts</h5>
                    <p>Last Sync: {handleIntegrationData()?.lastSynced}</p>
                    <Button
                      color="primary"
                      onClick={() => setIsExpanded(!isExpanded)}
                      variant="text" icon={<ChevronDownIcon className={isExpanded ? "rotate-180" : ""} />}>
                      {isExpanded ? "Hide Sync History" : "View Sync History"}
                    </Button>
                  </div>
                </div>
                <div className="flex lg:gap-2.5 flex-col lg:flex-row gap-2 items-start lg:items-end">
                  <Button
                    danger
                    icon={<CloseIcon className="w-2.5 h-2.5" />}
                    onClick={() => {
                      openDeleteModal(handleIntegrationData()?.integrationName);
                    }}
                  >
                    Remove
                  </Button>
                  {handleIntegrationData()?.integrationName === "googleanalytics" && (
                    <Button
                      type="default"
                      icon={<InfoSettings className="w-5 h-5" />}
                      onClick={() => navigate(`/integrations/custom-events-data/${handleIntegrationData()?.integrationName}`)}>
                      Map Custom Events
                    </Button>
                  )}
                  <Button
                    type="primary"
                    iconPosition="end"
                    icon={<ExternalLinkIcon className="w-5 h-5" />}
                    loading={isPostSchemaDesLoading}
                    onClick={async () => {
                      const integrationName = handleIntegrationData()?.integrationName;

                      // If it's a custom integration, call the schema API first
                      if (integrationName?.includes("custom")) {
                        try {
                          await postSchemaDes().unwrap();
                          toast.success('Schema description fetched successfully');
                          navigate(`/integrations/view-raw-data/${integrationName}`);
                        } catch (error) {
                          toast.error(error?.data?.error || 'Failed to fetch schema description');
                        }
                      } else {
                        // For non-custom integrations, navigate directly
                        navigate(`/integrations/view-raw-data/${integrationName}`);
                      }
                    }}>
                    {handleIntegrationData()?.integrationName?.includes("custom") ? "View Schema" : "View Raw Data"}
                  </Button>
                </div>

              </div>
            }
            onClose={closeDetailsOverlayModal}
            onSave={closeDetailsOverlayModal}
          >
            <div className="content-feature grid grid-cols-1 divide-y space-y-5 p-4 divide-gray-300">
              <div>
                <h4>About {handleIntegrationData()?.displayName}</h4>
                <p className="font-normal">We’ll alert you via <span className="font-medium">{userEmail}</span> if there is any unusual activity on your account.</p>
              </div>
              <div className="pt-4 px-4">
                <ul className="list-disc ml-2 text-sm text-gray-900">
                  {handleIntegrationData()?.aboutOauth?.length > 0 ? (
                    handleIntegrationData().aboutOauth.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))
                  ) : (
                    <li>No features available</li>
                  )}
                </ul>
              </div>
            </div>
            <div className="content-container border-t px-10 p-4 border-gray-300 grid grid-cols-1 divide-y space-y-1 divide-gray-300">
              <div className="pb-3">
                <h4>Sync History</h4>
                <p>We’ll alert you via <span className="font-medium">{userEmail}</span> if there is any unusual activity on your account.</p>
              </div>
              {handleIntegrationData()?.syncHistory?.data?.length > 0 ? (
                handleIntegrationData()?.syncHistory?.data.map((entry, index) => (
                  <div key={index} className="flex gap-3 items-center py-5">
                    <p className="font-medium">{new Date(entry.date)
                      .toLocaleString('en-US',
                        {
                          year: 'numeric',
                          month: 'short',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        }).replace(',', ' at')}
                    </p>
                    {index === 0 && <Badge title='Last Sync' variant='active' />}
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500  py-5">No sync history available</p>
              )}
            </div>
          </ModalBottomOverlay>
        )}

      {isDeleteOpen && (
        <Modal
          onClose={() => {
            closeDeleteModal();
          }}
          onSave={() => {
            handleRemove();
          }}
          className="xl:max-w-[60%] lg:max-w-[70%] md:max-w-[75%]"
        >
          <div className="flex flex-row items-start gap-5">
            <IconRings numRings={5} ringBaseSize={48} ringSpacing={56} className=" -z-10">
              <div className="bg-[#FEE4E2] p-3 w-fit h-fit rounded-full">
                <TrashIcon className="text-[#D92D20] w-5 h-5" />
              </div>
            </IconRings>
            {/* <div className="bg-[#FEE4E2] p-3 h-fit rounded-full">
              <TrashIcon className="text-[#D92D20] w-5 h-5" />
            </div> */}
            <div className="z-10">
              <h4 className=" text-nowrap">Delete Integration</h4>
              <p className="text-sm">Are you sure you want to delete this integration? This action cannot be undone.</p>
            </div>
          </div>
          <div className="flex flex-row justify-between items-center pt-10">
            <Checkbox
              control={control}
              label={'Don’t show again'}
              name="dontShowAgain"
              rules={{
                required: false,
              }}
            />
            <div className="space-x-4">
              <Button
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent backdrop closure
                  closeDeleteModal();
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent backdrop closure
                  handleRemove();
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      )}
      <Drawer
        closable={false}
        open={isBigQueryIntegrationModalOpen}
        style={{
          top: 0,
          padding: 0,
          maxWidth: '100vw',
        }}
        bodyStyle={{
          padding: 0,
          borderRadius: 0,
        }}
        width="100vw"
        className="rounded-none"
      >
        <Button
          className="absolute top-0 right-0 mt-2 mr-2"
          shape="circle"
          icon={<CloseIcon />}
          onClick={closeBigQueryIntegrationModal}
          type="text"
        />
        <>
          <BigQueryStepComponent
            closeBigQueryIntegrationModal={closeBigQueryIntegrationModal}
            control={control}
            watch={watch}
            reset={reset}
            handleRemove={handleRemove}
            getValues={getValues}
          />
        </>
      </Drawer>
    </>
  );
};

const BigQueryStepComponent = ({ control, watch, reset, getValues, closeBigQueryIntegrationModal, handleRemove }) => {

  const [step, setStep] = useState(1);
  const [renderedStep, setRenderedStep] = useState(1);
  const [fourTableName, setFourTableName] = useState("");
  const [isInitialSaveComplete, setIsInitialSaveComplete] = useState(false);
  const [hasViewedSchema, setHasViewedSchema] = useState(false);

  const navigate = useNavigate();


  const methodsCustom = useForm({
    defaultValues: {
      fields: '',
      templateName: '',
      checkedKeys: []
    },
    mode: 'onChange',
  });

  const { watch: customWatch, setValue: customSetValue, control: customControl, handleSubmit: customHandleSubmit } = methodsCustom;



  const containerRef = useRef(null);
  const innerRef = useRef(null);

  const [saveBigQueryProjectId, { isLoading: isSaveBigQueryProjectIdLoading }] = useSaveBigQueryProjectIdMutation();
  const [checkAccessInUserBigquery, { isLoading: isCheckAccessInUserBigqueryLoading }] = useCheckAccessInUserBigqueryMutation();

  const { data: bigQueryDatasetDropdown, isLoading: isBigQueryDatasetDropdownLoading } = useGetBigQueryDatasetDropdownQuery({},
    {
      skip: step !== 3, // Only fetch when on step 3
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  )

  const { data: getTableNames, isLoading: tableNamesLoading, isFetching: tableNamesFetching, refetch: tableNamerefretch } = useGetBigQueryProjectIdStatusQuery({ dataset_id: fourTableName }, {
    skip: !fourTableName,
  })

  // This query fetches the list of tables in a dataset for a specific integration tool
  // It should be skipped until we have both dataset_id and are on step 3
  const { data: checkBigQueryStatus, isLoading: isCheckBigQueryStatusLoading, isFetching: isCheckBigQueryStatusFetching } = useListTablesInDatasetQuery(
    {
      integration_tool: 'Meta', // Default to Meta, but this could be dynamic based on selected integration
      dataset_id: fourTableName
    },
    {
      skip: !fourTableName || step < 3, // Only fetch when we have a dataset selected and are on step 3
    }
  )

  const [saveTableNameUsingDataSet, { isLoading: saveTableNameUsingDataSetLoading }] = useSaveTableNameUsingDataSetMutation();

  const [postSchema, { data: postSchemaRes, error, isLoading: isPostSchemaLoading, isSuccess, isError }] = usePostSchemaDesMutation();




  // useEffect(() => {
  //   if (postSchemaRes?.success) {
  //     navigate(`/integrations/view-raw-data/custom-meta`);
  //     //  setStep(4);
  //     //  setRenderedStep(4);
  //   }
  //   if(error){
  //     console.log("error",error);

  //   }
  // }, [postSchemaRes,error])

  const [addCustomDatasetId, { isLoading: isAddCustomDatasetIdLoading, isSuccess: isAddCustomDatasetIdSuccess }] = useSelectDatasetForToolMutation();

  const SERVICE_EMAIL = 'oogwai-analytics-backend@oogwai-analytics.iam.gserviceaccount.com';

  const handleStep1 = async () => {
    // No validation or API call for Step 1 → Step 2
    return true;
  };

  const handleStep2 = async (values) => {

    if (!values.project_id?.trim()) {
      toast.error('Project ID cannot be empty');
      return false;
    }

    try {
      const response = await saveBigQueryProjectId({
        project_id: values.project_id.trim(),
        user_email: SERVICE_EMAIL,
      });
      if (response.error) {
        throw response.error;
      } else {
        const checkAccessResponse = await checkAccessInUserBigquery({
          project_id: values.project_id.trim(),
          user_email: SERVICE_EMAIL,
        });
        if (checkAccessResponse.error) {
          throw checkAccessResponse.error;
        } else {
          toast.success(checkAccessResponse?.data?.message || 'Access checked successfully');
        }
        toast.success(response?.data?.message || 'BigQuery project ID saved successfully');
      }
      return true;
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.data?.message || 'Failed to save BigQuery project ID');
      return false;
    }
  };


  const tableSchemaData = useMemo(() => {
    try {
      // Get tables from both queries
      const accessibleTables = getTableNames?.accessible_tables || [];
      const bigQueryTables = checkBigQueryStatus?.tables || checkBigQueryStatus?.accessible_tables || [];

      // Merge both data sources, preferring checkBigQueryStatus if available
      const tablesToUse = bigQueryTables.length > 0 ? bigQueryTables : accessibleTables;

      return tablesToUse.map(res => ({
        table_name: typeof res === 'string' ? res : res.table_name || res.name,
        description: typeof res === 'object' ? res.description || '' : '',
      }));
    } catch (error) {
      console.error('Error getting table names:', error);
      toast.error(error?.response?.data?.message || error?.data?.message || 'Failed to get table names');
      return [];
    }
  }, [getTableNames?.accessible_tables, checkBigQueryStatus, fourTableName]);

  const handleSaveBigQueryData = async (data) => {
    try {
      const formData = data?.data;
      const originalData = tableSchemaData || [];

      const fields = originalData.map((item, index) => {
        const formItem = formData?.[index] || {};

        return {
          key: index,
          table_name: item.table_name,
          description:
            formItem?.description !== undefined && formItem?.description !== null
              ? formItem.description
              : item.description || ''
        };
      });

      let val = customWatch('checkedKeys') || [];

      const payload = {
        templateName: fourTableName,
        fields: fields?.filter(field =>
          val?.some(item => item.key === field.key)
        )
      };

      const res = await saveTableNameUsingDataSet(payload).unwrap();
      if (res?.error) {
        throw new Error(res.error);
      }

      // Mark initial save as complete to show "View Schema" button
      setIsInitialSaveComplete(true);

      // setStep(1);
      // setRenderedStep(1);
      // closeBigQueryIntegrationModal();
      toast.success('Event Data Saved Successfully');
    } catch (err) {
      console.log(err);
      toast.error(`Error saving event data ${(err)?.data?.error || ''}`);
    }
  };

  const changeStep = async (newStep) => {
    if (newStep === renderedStep) return;

    const values = getValues();

    let canProceed = true;

    if (step === 1 && newStep === 2) {
      canProceed = await handleStep1();
    }

    if (step === 2 && newStep === 3) {
      canProceed = await handleStep2(values);
    }

    if (step === 3 && newStep === 4) {
      setStep(1);
      setRenderedStep(1);
      closeBigQueryIntegrationModal();
      return;
    }

    if (!canProceed) return;

    const container = containerRef.current;
    const inner = innerRef.current;
    if (!container || !inner) return;

    const startHeight = container.offsetHeight;
    container.style.height = `${startHeight}px`;

    setRenderedStep(newStep);

    requestAnimationFrame(() => {
      const endHeight = inner.offsetHeight;

      gsap.to(container, {
        height: endHeight,
        duration: 0.4,
        ease: 'power2.inOut',
        onComplete: () => {
          container.style.height = 'auto';
        },
      });
    });

    setStep(newStep);
  };

  const handleAddIntegration = async (integrationType) => {
    if (integrationType) {
      try {
        const response = await addCustomDatasetId({
          dataset_id: fourTableName,
          integration_tool: integrationType,
          user_email: SERVICE_EMAIL,
        });

        if (response.error) {
          throw response.error;
        }
        toast.success(`${integrationType.toUpperCase()} Integration added successfully`);
      } catch (error) {
        console.error('Error adding integration:', error?.data?.error);
        toast.error(error?.data?.error || 'Failed to add integration');
      }
    }
  }

  const customSetValueFn = useCallback((value) => {
    customSetValue('checkedKeys', tableNamesFetching ? [] : value);
  }, [customSetValue, tableNamesFetching]);


  return (
    <div className="flex-col justify-between items-start mt-10 mx-8">
      <h4 className="text-lg">Add Custom Data Source</h4>
      <h5 className="font-normal">Please upload the JSON with authentication details.</h5>

      <div className={(renderedStep === 3 || renderedStep === 4) ? " mt-14 flex flex-col" : "max-w-[512px] mx-auto mt-14 flex flex-col"}>
        <div
          ref={containerRef}
          className="overflow-hidden border border-gray-300 rounded-t-2xl"
        >
          <div ref={innerRef} className="flex flex-col gap-5 p-4">
            {renderedStep === 1 && (
              <>
                <h4 className="text-lg">Grant Permissions to the below email in your big query setup</h4>
                <div className="flex items-center">
                  <InputField
                    label="Service Mail"
                    name="service_mail"
                    value={'oogwai-analytics-backend@oogwai-analytics.iam.gserviceaccount.com'}
                    control={control}
                    placeholder="Enter Service Mail"
                    className="z-10"
                    suffix={
                      <HelpIcon
                        className="cursor-pointer text=[#A4A7AE]"
                        onClick={() => { }}
                      />
                    }
                    required={
                      { required: 'Service Mail is required' }
                    }
                  />
                  <Button
                    type="text"
                    icon={<CopyIcon />}
                    className="mt-[25px] p-6 border border-gray-300 pl-12 py-[22px] -ml-9"
                    onClick={() => {
                      const textArea = document.createElement('textarea');
                      textArea.value = SERVICE_EMAIL;
                      document.body.appendChild(textArea);
                      textArea.select();
                      document.execCommand('copy');
                      textArea.remove();
                      toast.success(`${SERVICE_EMAIL} copied to clipboard`);
                    }}
                  >
                    Copy Email
                  </Button>
                </div>
                <div>
                  <h5>How this works?</h5>
                  <Steps
                    current={null}
                    direction="vertical"
                    size="large"
                    className="pt-1"
                    items={[
                      {
                        title: (
                          <p className="flex items-center gap-2 step-content">
                            Go to
                            <a
                              className="text-green-600 hover:text-green-700 font-medium cursor-pointer"
                              href="https://console.cloud.google.com/"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Google Cloud Console
                            </a>
                            <RightArrowIcon className="w-3" /> <span className="font-medium">IAM and admin</span>
                            <RightArrowIcon className="w-3" /> <span className="font-medium">IAM</span>
                          </p>
                        ),
                      },
                      {
                        title: (
                          <p className="step-content">
                            Click <span className="font-medium">Grant Access</span>
                          </p>
                        ),
                      },
                      {
                        title: (
                          <p className="step-content">
                            Paste <span className="font-medium">Service Email</span> in the <span className="font-medium">Add Principals</span> Field
                          </p>
                        ),
                      },
                      {
                        title: (
                          <p className="step-content">
                            In <span className="font-medium">Assign Roles</span> Select the following roles
                            <div className="bg-gray-100 border border-gray-200 w-fit p-4 rounded-xl mt-3">
                              <ul className="list-disc ml-4 text-sm font-medium text-gray-900">
                                <li>bigquery.dataViewer</li>
                                <li>bigquery.jobUser</li>
                                <li>bigquery.metadataViewer</li>
                                <li>resourcemanager.projectIamAdmin</li>
                                <li>bigquery.dataEditor</li>
                              </ul>
                            </div>
                          </p>
                        ),
                      },
                      {
                        title: (
                          <p className="step-content font-medium">
                            Save
                          </p>
                        ),
                      },
                    ]}
                  />
                </div>
              </>
            )}

            {renderedStep === 2 && (
              <>
                <h4 className="text-lg">Select the project you want to connect</h4>
                <InputField
                  label="Project ID"
                  name="project_id"
                  control={control}
                  placeholder="Enter Project ID"
                />
              </>
            )}

            {renderedStep === 3 && (
              <>
                <div className=''>

                  <h4 className="text-lg">Select your Data Template</h4>
                  {/* <Controller
                    control={control}
                    name="selected_dataset"
                    render={({ field, fieldState: { error: fieldError } }) => ( */}
                  <Select
                    // defaultValue={field.value}
                    value={fourTableName}
                    loading={isBigQueryDatasetDropdownLoading}
                    size="medium"
                    placeholder="Select a dataset"
                    className="w-3/12"
                    onChange={(e) => {
                      setFourTableName(e)
                      setIsInitialSaveComplete(false) // Reset save state when dataset changes
                      setHasViewedSchema(false) // Reset viewed schema state when dataset changes
                      // field.onChange(e);
                    }}
                    allowClear
                  // onChange={(value) => setSelectedDataset(value)}
                  >
                    {bigQueryDatasetDropdown?.datasets?.map((option) => (
                      <Select.Option key={option} value={option}>
                        {option}
                      </Select.Option>
                    ))}
                  </Select>
                  {/* )}
                  /> */}
                </div>

                <div className='mb-12'>
                  {
                    (tableNamesLoading || tableNamesFetching || isCheckBigQueryStatusLoading || isCheckBigQueryStatusFetching) ? <p className="text-2xl font-semibold text-gray-500 text-center">{'Fetching table data please wait...'}</p> :

                      <DynamicTable
                        data={tableSchemaData}
                        control={customControl}
                        loading={tableNamesLoading || tableNamesFetching || isCheckBigQueryStatusLoading || isCheckBigQueryStatusFetching}
                        fieldConfig={
                          {
                            "description": {
                              "type": "text-area",
                              "placeholder": "Enter a description...",
                            },
                          }
                        }
                        customSetValueFn={customSetValueFn}

                      />
                  }

                </div>

                {/* <div className="grid grid-cols-2 gap-4">
                  <Cards
                    className={`p-4 gap-3 border border-gray-300 rounded-lg cursor-pointer`}
                    variant="secondary"
                    childClassName="flex-col items-start gap-4"
                
                  >
                    <div className={'flex justify-start items-center gap-1'}>
                      <BrandShopify className="w-12 h-auto" />
                      <h5 className="font-medium text-text-700 font-albert">Shopify</h5>
                    </div>
                    <div className="w-full flex items-center justify-between gap-2">
                      <Button
                        variant={'primary'}
                        disabled={!watch('selected_dataset') && !(customIntegrationStatus?.['Shopify'] !== '')}
                        icon={customIntegrationStatus?.['Shopify'] !== '' ? <CheckIcon className={"h-3.5 w-3.5"} /> : <PlusIcon className="h-3.5 w-3.5" />}
                        loading={isAddCustomDatasetIdLoading}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (customIntegrationStatus?.['Shopify'] === '') {
                            handleAddIntegration('Shopify');
                          } else {
                            handleRemove('custom-shopify');
                          }
                        }}
                      >
                        {customIntegrationStatus?.['Shopify'] !== '' ? 'Added' : 'Add to Oogwai'}
                      </Button>
                    </div>
                  </Cards>
                  <Cards
                    className={`p-4 gap-3 border border-gray-300 rounded-lg cursor-pointer`}
                    variant="secondary"
                    childClassName="flex-col items-start gap-4"
                  >
                    <div className={'flex justify-start items-center gap-1'}>
                      <BrandMagento className="w-12 h-auto" />
                      <h5 className="font-medium text-text-700 font-albert">Magento</h5>
                    </div>
                    <Button
                      variant={'primary'}
                      disabled={!watch('selected_dataset') && !(customIntegrationStatus?.['Magento'] !== '')}
                      icon={customIntegrationStatus?.['Magento'] !== '' ? <CheckIcon className={"h-3.5 w-3.5"} /> : <PlusIcon className="h-3.5 w-3.5" />}
                      loading={isAddCustomDatasetIdLoading}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (customIntegrationStatus?.['Magento'] === '') {
                          handleAddIntegration('Magento');
                        } else {
                          handleRemove('custom-magento');
                        }
                      }
                      }
                    >
                      {customIntegrationStatus?.['Magento'] !== '' ? 'Added' : 'Add to Oogwai'}
                    </Button>
                  </Cards>
                  <Cards
                    className={`p-4 gap-3 border border-gray-300 rounded-lg cursor-pointer`}
                    variant="secondary"
                    childClassName="flex-col items-start gap-4"
                  >
                    <div className={'flex justify-start items-center gap-1'}>
                      <BrandMeta className="w-12 h-auto" />
                      <h5 className="font-medium text-text-700 font-albert">Meta</h5>
                    </div>
                    <Button
                      variant={'primary'}
                      disabled={!watch('selected_dataset') && !(customIntegrationStatus?.['Meta'] !== '')}
                      icon={customIntegrationStatus?.['Meta'] !== '' ? <CheckIcon className={"h-3.5 w-3.5"} /> : <PlusIcon className="h-3.5 w-3.5" />}
                      loading={isAddCustomDatasetIdLoading}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (customIntegrationStatus?.['Meta'] === '') {
                          handleAddIntegration('Meta');
                        } else {
                          handleRemove('custom-meta');
                        }
                      }
                      }
                    >
                      {customIntegrationStatus?.['Meta'] !== '' ? 'Added' : 'Add to Oogwai'}
                    </Button>
                  </Cards>
                </div> */}


                {isAddCustomDatasetIdLoading && (<p className='text-sm'>Fetching table data please wait</p>)}
              </>)
            }


          </div>
        </div>

        <div className={step === 3 ? "fixed bottom-0 left-0 w-full bg-white border-t flex items-center justify-center py-2" : "bg-gray-50 border border-gray-300 rounded-b-2xl border-t-0 p-4"}>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => {
              closeBigQueryIntegrationModal()
              reset();
              setStep(1);
              setRenderedStep(1);
              setIsInitialSaveComplete(false);
              setHasViewedSchema(false);
            }
            }>
              Cancel
            </Button>
            {step > 1 && (
              <Button onClick={() => {
                reset();
                changeStep(step - 1);
              }}>
                Back
              </Button>
            )}
            {step === 1 && (
              <Button type="primary" onClick={() => changeStep(checkBigQueryStatus?.project_id ? step + 2 : step + 1)}>
                Next
              </Button>
            )}
            {step === 2 && (
              <Button type="primary" loading={isCheckAccessInUserBigqueryLoading} onClick={() => changeStep(step + 1)}>
                Save Settings
              </Button>
            )}

            {step === 3 && !hasViewedSchema && (
              <Button
                type="primary"
                loading={isPostSchemaLoading}
                disabled={tableNamesLoading || tableNamesFetching || isCheckBigQueryStatusLoading || isCheckBigQueryStatusFetching || tableSchemaData.length === 0}
                onClick={async () => {
                  try {
                    await handleSaveBigQueryData(tableSchemaData);
                    toast.success('Schema description fetched successfully');
                    setHasViewedSchema(true);
                    navigate(`/integrations/view-raw-data/custom-meta`);
                  } catch (err) {
                    console.error('Mutation error:', err);
                    toast.error(err?.data?.error || 'Failed to fetch schema description');
                  }
                }}
              >
                Save & View Schema
              </Button>
            )}

            {step === 3 && hasViewedSchema && (
              <Button
                type="primary"
                loading={saveTableNameUsingDataSetLoading}
                disabled={tableNamesLoading || tableNamesFetching || isCheckBigQueryStatusLoading || isCheckBigQueryStatusFetching || tableSchemaData.length === 0}
                onClick={customHandleSubmit(handleSaveBigQueryData)}
              >
                Save Changes
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
